import {
  AlertCircle,
  Check,
  ClipboardList,
  Copy,
  LoaderCircle,
  MessageCircle,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  answerFromEvidence,
  buildHumanHandoffSummary,
  getSupportStatus,
  requestSupportAgent,
} from "../lib/support";
import type {
  DecisionPlan,
  LocationNode,
  QianfanSupportStatus,
  SupportLocalAnswer,
  SupportServerStatus,
  WarehouseDataset,
} from "../types";

interface SupportWidgetProps {
  open: boolean;
  launcherVisible?: boolean;
  onOpen: () => void;
  onClose: () => void;
  dataset: WarehouseDataset;
  plan: DecisionPlan;
  locations: LocationNode[];
}

interface SupportMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  evidenceAnswer?: string;
  tags?: string[];
  createdAt: string;
  source?: "local" | "qianfan" | "pending";
  question?: string;
  providerDetail?: string;
  requestId?: string;
}

const STORAGE_KEY = "cangxu-support-chat-v1";
const MAX_STORED_MESSAGES = 36;

const defaultProvider: QianfanSupportStatus = {
  configured: false,
  mode: "unavailable",
  verified: false,
  detail: "正在检查千帆 Agent 服务端状态。",
};

function messageId() {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `message-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function loadMessages(): SupportMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((message): message is SupportMessage => {
        if (!message || typeof message !== "object") return false;
        const item = message as Partial<SupportMessage>;
        return (
          (item.role === "user" || item.role === "assistant") &&
          typeof item.text === "string" &&
          typeof item.id === "string" &&
          typeof item.createdAt === "string"
        );
      })
      .slice(-MAX_STORED_MESSAGES)
      .map((message) =>
        message.source === "pending"
          ? {
              ...message,
              source: "local",
              providerDetail: "上次千帆增强请求未完成，已保留本地证据答案。",
            }
          : message,
      );
  } catch {
    return [];
  }
}

function persistMessages(messages: SupportMessage[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_STORED_MESSAGES)));
  } catch {
    // The conversation remains available in memory when browser storage is full.
  }
}

async function copyToClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.append(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("copy_failed");
}

function providerLabel(provider: QianfanSupportStatus) {
  if (provider.mode === "degraded") return "本地降级";
  if (provider.configured && provider.verified) return "千帆 Agent 在线";
  if (provider.configured) return "千帆已配置";
  return "本地证据模式";
}

function providerTone(provider: QianfanSupportStatus) {
  if (provider.mode === "degraded") return "warn";
  if (provider.configured) return "ready";
  return "local";
}

function formatTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", hour12: false });
}

export function SupportWidget({
  open,
  launcherVisible = true,
  onOpen,
  onClose,
  dataset,
  plan,
  locations,
}: SupportWidgetProps) {
  const [messages, setMessages] = useState<SupportMessage[]>(loadMessages);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState<QianfanSupportStatus>(defaultProvider);
  const [notice, setNotice] = useState("");
  const [handoffOpen, setHandoffOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const conversationRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef<AbortController | null>(null);

  const suggestedQuestions = useMemo(
    () => [
      "为什么这款商品被判为爆旺？",
      "当前库位为什么建议搬到近端？",
      "哪些批次临近过期？",
      "这款商品为什么按 7 日补货？",
      "我的数据是否足以支持这个结论？",
      "如何查看二维库区和导入商品数据？",
    ],
    [],
  );

  const handoffSummary = useMemo(
    () =>
      buildHumanHandoffSummary(
        dataset,
        plan,
        messages.map((message) => ({ role: message.role, text: message.text })),
      ),
    [dataset, messages, plan],
  );

  useEffect(() => {
    persistMessages(messages);
  }, [messages]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    getSupportStatus(controller.signal).then((status: SupportServerStatus) => {
      if (!controller.signal.aborted) setProvider(status.qianfan);
    });
    return () => controller.abort();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 40);

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [onClose, open]);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      conversationRef.current?.scrollTo({
        top: conversationRef.current.scrollHeight,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [busy, messages, open]);

  useEffect(
    () => () => {
      requestRef.current?.abort();
    },
    [],
  );

  function updateProviderFromResponse(status: QianfanSupportStatus) {
    setProvider(status);
  }

  async function enhanceWithAgent(question: string, local: SupportLocalAnswer, assistantId: string) {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    try {
      const response = await requestSupportAgent(
        {
          message: question,
          local,
          history: messages
            .filter((message) => message.role === "user" || message.text)
            .slice(-6)
            .map((message) => ({ role: message.role, content: message.text })),
        },
        controller.signal,
      );
      if (controller.signal.aborted) return;
      updateProviderFromResponse(response.provider);
      setMessages((current) =>
        current.map((message) =>
          message.id === assistantId
            ? {
                ...message,
                text: response.agentAnswer ?? local.answer,
                evidenceAnswer: local.answer,
                tags: local.tags,
                source: response.mode === "qianfan" ? "qianfan" : "local",
                providerDetail: response.provider.detail,
                requestId: response.requestId,
              }
            : message,
        ),
      );
    } catch (error) {
      if (controller.signal.aborted) return;
      const detail =
        error instanceof Error && error.message.includes("频繁")
          ? "提问过于频繁，已保留本地证据答案。"
          : "客服服务暂时不可用，已保留本地证据答案。";
      setProvider({
        configured: provider.configured,
        mode: "degraded",
        verified: false,
        detail,
      });
      setMessages((current) =>
        current.map((message) =>
          message.id === assistantId
            ? {
                ...message,
                source: "local",
                evidenceAnswer: local.answer,
                tags: local.tags,
                providerDetail: detail,
              }
            : message,
        ),
      );
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      if (!controller.signal.aborted) setBusy(false);
    }
  }

  function submitQuestion(question: string) {
    const cleaned = question.trim();
    if (!cleaned || busy) return;
    const local = answerFromEvidence(cleaned, dataset, plan, locations);
    const assistantId = messageId();
    const createdAt = new Date().toISOString();
    const nextMessages: SupportMessage[] = [
      ...messages,
      {
        id: messageId(),
        role: "user",
        text: cleaned,
        createdAt,
      },
      {
        id: assistantId,
        role: "assistant",
        text: local.answer,
        evidenceAnswer: local.answer,
        tags: local.tags,
        createdAt,
        source: "pending",
        question: cleaned,
        providerDetail: "正在核对当前数据，并尝试由千帆 Agent 组织回答。",
      },
    ];
    setMessages(nextMessages);
    setDraft("");
    setNotice("");
    setHandoffOpen(false);
    setBusy(true);
    void enhanceWithAgent(cleaned, local, assistantId);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    submitQuestion(draft);
  }

  function retry(message: SupportMessage) {
    if (!message.question || busy) return;
    const local = answerFromEvidence(message.question, dataset, plan, locations);
    setMessages((current) =>
      current.map((item) =>
        item.id === message.id
          ? {
              ...item,
              text: local.answer,
              evidenceAnswer: local.answer,
              tags: local.tags,
              source: "pending",
              providerDetail: "正在重新核对当前数据并请求千帆 Agent。",
            }
          : item,
      ),
    );
    setBusy(true);
    setNotice("");
    void enhanceWithAgent(message.question, local, message.id);
  }

  async function copyAnswer(text: string) {
    try {
      await copyToClipboard(text);
      setNotice("回答已复制");
    } catch {
      setNotice("复制失败，请手动选择文字");
    }
    window.setTimeout(() => setNotice(""), 2200);
  }

  async function copyHandoff() {
    try {
      await copyToClipboard(handoffSummary);
      setNotice("人工接手摘要已复制；未创建工单");
    } catch {
      setNotice("摘要复制失败，请在预览中手动复制");
    }
    window.setTimeout(() => setNotice(""), 2600);
  }

  function clearConversation() {
    if (!messages.length) return;
    const confirmed = window.confirm("清除当前浏览器中的全部客服对话？此操作不会影响导入的仓库数据。");
    if (!confirmed) return;
    setMessages([]);
    setHandoffOpen(false);
    setNotice("本地对话已清除");
    window.setTimeout(() => setNotice(""), 2200);
  }

  return (
    <>
      {launcherVisible && (
        <button
          className="support-launcher"
          type="button"
          aria-label="打开仓储客服"
          aria-expanded={open}
          onClick={onOpen}
        >
          <span className="support-launcher-icon">
            <MessageCircle size={20} />
            <i aria-hidden="true" />
          </span>
          <span>
            <strong>仓储客服</strong>
            <small>{provider.configured ? "千帆 Agent" : "数据问答"}</small>
          </span>
        </button>
      )}

      {open && (
        <div className="support-layer">
          <button className="support-scrim" type="button" aria-label="关闭仓储客服" onClick={onClose} />
          <div
            className="support-panel"
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="support-title"
          >
            <header className="support-header">
              <div className="support-identity">
                <span className="support-emblem" aria-hidden="true">
                  <Sparkles size={19} />
                </span>
                <div>
                  <span>SUPPORT AGENT</span>
                  <h2 id="support-title">仓储客服</h2>
                </div>
              </div>
              <div className="support-header-actions">
                <button
                  type="button"
                  className="support-tool"
                  aria-label="清除客服对话"
                  title="清除对话"
                  onClick={clearConversation}
                  disabled={!messages.length}
                >
                  <Trash2 size={16} />
                </button>
                <button
                  ref={closeRef}
                  type="button"
                  className="support-tool"
                  aria-label="关闭仓储客服"
                  onClick={onClose}
                >
                  <X size={17} />
                </button>
              </div>
            </header>

            <div className={`support-health support-health-${providerTone(provider)}`}>
              <span className="support-health-dot" aria-hidden="true" />
              <div>
                <strong>{providerLabel(provider)}</strong>
                <small>{provider.detail}</small>
              </div>
              <ShieldCheck size={16} aria-label="数据证据保护" />
            </div>

            <div className="support-conversation" ref={conversationRef} aria-live="polite">
              {!messages.length && (
                <div className="support-empty">
                  <div className="support-empty-mark" aria-hidden="true">
                    <MessageCircle size={24} />
                  </div>
                  <span>EVIDENCE-FIRST SUPPORT</span>
                  <h3>直接问数据，不必先找报表。</h3>
                  <p>
                    我会先核算当前数据快照，再说明趋势、库位、效期与补货建议。
                    千帆不可用时会保留同一份证据答案。
                  </p>
                  <div className="support-suggestions" aria-label="建议问题">
                    {suggestedQuestions.map((question) => (
                      <button key={question} type="button" onClick={() => submitQuestion(question)}>
                        {question}
                        <span aria-hidden="true">↗</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`support-message support-message-${message.role}`}
                >
                  <div className="support-message-meta">
                    <span>
                      {message.role === "user"
                        ? "你"
                        : message.source === "pending"
                          ? "证据引擎正在回答"
                          : message.source === "qianfan"
                            ? "千帆 Agent"
                            : "本地证据引擎"}
                    </span>
                    <time dateTime={message.createdAt}>{formatTime(message.createdAt)}</time>
                  </div>
                  <div className="support-message-body">
                    {message.source === "pending" && (
                      <span className="support-loading">
                        <LoaderCircle size={14} /> 正在核验数据并组织回答
                      </span>
                    )}
                    <p>{message.text}</p>
                    {message.tags?.length ? (
                      <div className="support-tags">
                        {message.tags.map((tag) => <span key={tag}>{tag}</span>)}
                      </div>
                    ) : null}
                    {message.role === "assistant" && message.evidenceAnswer && message.source === "qianfan" && (
                      <details className="support-evidence">
                        <summary>查看本地证据答案</summary>
                        <p>{message.evidenceAnswer}</p>
                      </details>
                    )}
                    {message.providerDetail && message.source !== "pending" && (
                      <small className="support-provider-detail">{message.providerDetail}</small>
                    )}
                  </div>
                  {message.role === "assistant" && message.source !== "pending" && (
                    <div className="support-message-actions">
                      <button type="button" onClick={() => void copyAnswer(message.text)}>
                        <Copy size={13} /> 复制
                      </button>
                      {message.question && (
                        <button type="button" onClick={() => retry(message)} disabled={busy}>
                          <RefreshCw size={13} /> 重试
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {handoffOpen && (
              <section className="support-handoff" aria-label="人工接手摘要">
                <div>
                  <ClipboardList size={16} />
                  <span>
                    <strong>人工接手摘要</strong>
                    <small>仅生成摘要，不会声称已接通人工或创建工单。</small>
                  </span>
                  <button type="button" onClick={() => void copyHandoff()}>复制摘要</button>
                </div>
                <pre>{handoffSummary}</pre>
              </section>
            )}

            <div className="support-notice" aria-live="polite">
              {notice && (
                <span>
                  {notice.includes("失败") ? <AlertCircle size={14} /> : <Check size={14} />}
                  {notice}
                </span>
              )}
            </div>

            <form className="support-composer" onSubmit={submit}>
              <textarea
                ref={composerRef}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    submitQuestion(draft);
                  }
                }}
                rows={2}
                maxLength={1200}
                placeholder="问商品、库位、批次效期或补货方案…"
                aria-label="向仓储客服提问"
              />
              <div className="support-composer-footer">
                <button
                  type="button"
                  className="support-handoff-button"
                  aria-expanded={handoffOpen}
                  onClick={() => setHandoffOpen((current) => !current)}
                >
                  <ClipboardList size={14} /> 接手摘要
                </button>
                <span>Enter 发送 · Shift + Enter 换行</span>
                <button
                  type="submit"
                  className="support-send"
                  aria-label="发送问题"
                  disabled={busy || !draft.trim()}
                >
                  {busy ? <LoaderCircle size={17} className="is-spinning" /> : <Send size={17} />}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
